import {
  type GetTenantResponse,
  GetTenantResponseFromJSON,
  type TenantApi,
} from "@/openapi/generated";

export type TenantWithRegion = GetTenantResponse & {
  state: string | null;
  country: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function readOptionalString(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  const record = asRecord(value);
  if (!record) return null;

  return (
    readOptionalString(record.code) ??
    readOptionalString(record.value) ??
    readOptionalString(record.name)
  );
}

function readRegionField(
  record: Record<string, unknown> | null,
  keys: string[],
): string | null {
  if (!record) return null;
  for (const key of keys) {
    const value = readOptionalString(record[key]);
    if (value) return value;
  }
  return null;
}

const STATE_KEYS = [
  "state",
  "State",
  "region",
  "Region",
  "province",
  "Province",
];
const COUNTRY_KEYS = [
  "country",
  "Country",
  "countryCode",
  "CountryCode",
];

export function attachTenantRegionFields(
  tenant: GetTenantResponse,
  json: unknown,
): TenantWithRegion {
  const record = asRecord(json);
  const address = asRecord(record?.address) ?? asRecord(record?.Address);

  const state =
    readRegionField(record, STATE_KEYS) ??
    readRegionField(address, STATE_KEYS) ??
    readOptionalString(tenant.state);

  const country =
    readRegionField(record, COUNTRY_KEYS) ??
    readRegionField(address, COUNTRY_KEYS) ??
    readOptionalString(tenant.country);

  return {
    ...tenant,
    state,
    country,
  };
}

export function coalesceTenantRegion(
  fresh: TenantWithRegion,
  previous?: TenantWithRegion | GetTenantResponse | null,
): TenantWithRegion {
  const previousState = getTenantState(previous);
  const previousCountry = getTenantCountry(previous);

  return {
    ...fresh,
    state: fresh.state ?? (previousState.length > 0 ? previousState : null),
    country: fresh.country ?? previousCountry,
  };
}

export async function fetchTenantAccount(
  service: TenantApi,
  tenantId: number,
  previous?: TenantWithRegion | GetTenantResponse | null,
): Promise<TenantWithRegion> {
  const response = await service.apiAccountTenantIdGetRaw({
    tenantId,
    xTenantID: Number(tenantId),
  });
  const json = await response.raw.json();
  return coalesceTenantRegion(
    attachTenantRegionFields(GetTenantResponseFromJSON(json), json),
    previous,
  );
}

export function getTenantState(
  tenant: GetTenantResponse | TenantWithRegion | null | undefined,
): string {
  const state = (tenant as TenantWithRegion | null | undefined)?.state;
  return typeof state === "string" ? state.trim() : "";
}

export function getTenantCountry(
  tenant: GetTenantResponse | TenantWithRegion | null | undefined,
): string | null {
  const country = (tenant as TenantWithRegion | null | undefined)?.country;
  if (typeof country !== "string") return null;
  const trimmed = country.trim();
  return trimmed.length > 0 ? trimmed : null;
}
